import { batchFileGitInfo } from "../../git.js";
import type { DriftIssue, StalenessThresholds } from "../../types.js";

/**
 * Check staleness of multiple scaffold files in a single batch.
 * Uses parallel git operations instead of sequential per-file calls.
 */
export async function checkStalenessBatch(
  filePaths: string[],
  cwd: string,
  thresholds: StalenessThresholds
): Promise<DriftIssue[]> {
  const issues: DriftIssue[] = [];
  const gitInfo = await batchFileGitInfo(filePaths, cwd);

  for (const source of filePaths) {
    const info = gitInfo.get(source);
    const days = info?.days ?? null;
    const commits = info?.commits ?? null;

    type Sev = "error" | "warning" | null;
    const daySev: Sev =
      days !== null && days >= thresholds.errorDays ? "error" :
      days !== null && days >= thresholds.warnDays ? "warning" : null;

    const commitSev: Sev =
      commits !== null && commits >= thresholds.errorCommits ? "error" :
      commits !== null && commits >= thresholds.warnCommits ? "warning" : null;

    const severity: Sev =
      daySev === "error" || commitSev === "error" ? "error" :
      daySev === "warning" || commitSev === "warning" ? "warning" : null;

    if (severity) {
      const parts: string[] = [];
      if (daySev) parts.push(`${days}d since last update`);
      if (commitSev) parts.push(`${commits} commits behind`);
      issues.push({
        code: "STALE_FILE",
        severity,
        file: source,
        line: null,
        message: parts.join(", "),
      });
    }
  }

  return issues;
}
